<template>
  <div class="details-container" v-if="currentUser">
    <div class="header">
      <div class="title-wrapper"><h4 class="title">{{ $t('titles.your_details') }}</h4></div>

      <div class="edit">
        <outline-button @click="edit" v-if="!editing">
          {{ $t('buttons.edit')}}
        </outline-button>
        <outline-button @click="cancel" v-else>
          {{ $t('buttons.cancel')}}
        </outline-button>
      </div>

    </div>
    <div :class="{'editingOn': editing}">
      <div class="edit-column">
        <div class="key">
          {{ $t('labels.first_name')}}<span v-if="editing"> *</span>
        </div>
        <div class="value">
          <span v-if="!editing">{{user.firstName}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.firstName" required @blur="check('firstName')" />
            <small class="error" v-if="errors.firstName">{{errors.firstName}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.last_name')}}<span v-if="editing"> *</span>
        </div>
        <div class="value">
          <span v-if="!editing">{{user.lastName}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.lastName" required @blur="check('lastName')" />
            <small class="error" v-if="errors.lastName">{{errors.lastName}}</small>
          </div>
        </div>
      </div>



      <div class="edit-column">
        <div class="key">
          {{ $t('labels.phone')}}
        </div>
        <div class="value">
          <span v-if="!editing">{{user.phone}}</span>
          <div class="edit-container" v-else>
            <vue-tel-input :default-country="defaultPhoneCountry"
              show-dial-code="true" v-model="user.phone" class="edit-input" mode="international"
              :preferred-countries="preferredCountries" valid-characters-only
              @validate="validatePhoneNumber"/>
            <small class="error" v-if="errors.phone">{{errors.phone}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.address')}}
        </div>
        <div class="value">
          <span v-if="!editing">{{user.address}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.address" />
            <small class="error" v-if="errors.address">{{errors.address}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.postal_code')}}<span v-if="editing"> *</span>
        </div>
        <div class="value">
          <span v-if="!editing">{{user.postalCode}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.postalCode" />
            <small class="error" v-if="errors.postalCode">{{errors.postalCode}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.city')}}
        </div>
        <div class="value">
          <span v-if="!editing">{{user.city}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.city" />
            <small class="error" v-if="errors.city">{{errors.city}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.country')}}<span v-if="editing"> *</span>
        </div>
        <div class="value">
          <span v-if="!editing">{{user.country}}</span>
          <div class="edit-container" v-else>
            <country-select :user="user"/>
            <small class="error" v-if="errors.country">{{errors.country}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column" v-if="stateRequired">
        <div class="key">
          {{ $t('labels.state')}}<span v-if="editing"> *</span>
        </div>
        <div class="value">
          <span v-if="!editing">{{user.state}}</span>
          <div class="edit-container" v-else>
            <state-select :user="user"/>
            <small class="error" v-if="errors.state">{{errors.state}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.dob')}}
        </div>
        <div class="value">
          <span v-if="!editing">{{user.dob}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="date" v-model="user.dob" />
            <small class="error" v-if="errors.dob">{{errors.dob}}</small>
          </div>
        </div>
      </div>

      <div class="edit-column">
        <div class="key">
          {{ $t('labels.company_name')}}
        </div>
        <div class="value">
          <span v-if="!editing">{{user.companyName}}</span>
          <div class="edit-container" v-else>
            <input class="edit-input" type="text" v-model="user.companyName" required @blur="check('companyName')" />
            <small class="error" v-if="errors.companyName">{{errors.companyName}}</small>
          </div>
        </div>
      </div>

    </div>

    <div v-if="editing" class="save-btn-wrapper mt-3">
      <primary-button @click="save" cta>{{ $t('buttons.save') }}</primary-button>
    </div>

    <div class="edit-wrapper-mob">
      <div class="edit-mob">
        <outline-button @click="edit" v-if="!editing">
          {{$t('buttons.edit')}}
        </outline-button>
        <outline-button @click="cancel" v-else>
          {{ $t('buttons.cancel')}}
        </outline-button>
      </div>
    </div>
  </div>
</template>

<script>
import PrimaryButton from '@/components/buttons/PrimaryButton.vue'
import OutlineButton from '@/components/buttons/OutlineButton.vue'
import CountrySelect from "@/components/input/CountrySelect";
import StateSelect from "@/components/input/StateSelect";


export default {
  data() {
    return {
      errors: {
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
        address: false,
        postalCode: false,
        city: false,
        country: false,
        state: false,
        dob: false,
        companyName: false,
      },
      editing: false,
      user: this.populateUser(this.$store.getters.currentUser),
      phoneValidation: {
        valid: true,
      },
    }
  },
  computed: {
    defaultPhoneCountry() {
      return this.user.country || process.env.FG_DEFAULT_COUNTRY
    },
    currentUser () {
      return this.$store.getters.currentUser
    },
    stateRequired() {
      return this.user.country == 'US';
    },
    preferredCountries() {
      return [ process.env.FG_DEFAULT_COUNTRY ];
    }
  },
  methods: {
    validatePhoneNumber(payload) {
      this.phoneValidation = payload;
    },
    edit() {
      this.editing = true
      this.user.country = this.user.country || (process.env.FG_DEFAULT_COUNTRY || null)
    },
    cancel () {
      this.editing = false
      this.user = this.populateUser(this.currentUser)
    },
    populateUser (current) {
      return {
        firstName: current.firstName,
        lastName: current.lastName,
        email: current.email,
        phone: current.phone,
        address: current.address,
        postalCode: current.postalCode,
        city: current.city,
        country: current.country,
        state: current.state,
        dob: current.dob,
        companyName: current.companyName,
      }
    },
    check(field) {
      const requiredFields = [
          'firstName',
          'lastName',
          'email',
          // 'phone',
          // 'address',
          'postalCode',
          // 'city',
          'country',
          // 'state'
      ];
      if (field == 'phone') {
        this.errors[field] = (this.user.phone!='' && this.user.phone != null) && !this.phoneValidation.valid ? this.$t('errors.invalid_phone') : false;
        return;
      }
      if (field == 'state') {
        this.user.state = !this.stateRequired ? '' : this.user.state
        this.errors[field] = !this.stateRequired || this.user[field] ? false : this.$t('errors.required')
        return
      }
      this.errors[field] = this.user[field] || !requiredFields.includes(field) ? false : this.$t('errors.required')
    },
    hasErrors() {
      return Object.values(this.errors).find(i => i != false) != null;
    },
    save() {
      Object.keys(this.user).forEach(field => {
        this.check(field)
      })
      if (this.hasErrors()) {
        return
      }
      this.editing = false

      const updatedFields = {
        ...this.user,
        dob: this.user.dob ? this.user.dob : null,
      }
      this.$store.dispatch('updateUser', updatedFields)
          .then(() => {
            this.$store.dispatch('success', this.$t('messages.account_updated'))
          })
          // .catch(err => {
          .catch(() => {
            //
          })
    },
  },
  components: {
    CountrySelect,
    StateSelect,
    PrimaryButton,
    OutlineButton
  }
}
</script>


<style lang="scss" scoped>
  .value small.error {
    display: block !important;
  }
  .details-container {
    width: 100%;
    margin-top: 30px;

    .header {
      display: flex;
      .title-wrapper {
        flex: 1
      }
      .edit {
        flex: 1;
        text-align: right;
        align-self: center;
        @media screen and (max-width: 750px) {
          display: none;
        }
      }
    }

    .save-btn-wrapper {
      text-align: right;
      @media screen and (max-width: 750px) {
        text-align: center;
      }
    }
    .edit-container {
      .edit-input {
        border: none;
        padding: 0;
        //font-family: $secondary-font-family;
        font-size: 1rem;
        width: 100%;
      }
      select {
        appearance: none;
        border: 0;
        background-color: transparent;
      }

    }
    .edit-wrapper-mob {
      .edit-mob {
        text-align: center;
        margin: 20px 0;
      }
      @media screen and (min-width: 750px) {
        display: none;
      }
    }
  }

  .edit-column {
    display: flex;
    font-family: $base-font-family;
    font-size: 1rem;
    border-bottom: solid 1px $gray-2;
    padding: 10px 0;
    transition : border 500ms ease-out;
    .key {
      flex: 0 1 23%;
      //font-weight: bold;
      //// font-size: 0.875rem;
      //text-transform: uppercase;
      @media screen and (max-width: 750px) {
        flex: 0 1 30%;
      }
    }
    .value {
      flex: 1 1 77%;
      font-size: 1rem;
      input, select {
        background-color: transparent;
        width: 100%;

        &::-webkit-datetime-edit-fields-wrapper {
          padding: 0;
        }

        &::-webkit-calendar-picker-indicator {
          display: none;
        }
      }

      @media screen and (max-width: 750px) {
        flex: 0 1 70%;
        overflow:hidden;
        white-space:nowrap;
        text-overflow:ellipsis;
      }

      & /deep/ .vti__dropdown {
        @media screen and (max-width: 750px) {
          white-space: normal;
        }
      }
    }

    & /deep/.vti__dropdown {
      padding: 0;
    }
    & /deep/.vti__input {
      padding: 0 0 0 8px;
      background-color: transparent;
    }

    .editingOn & {
      border-bottom: 1px solid $primary-btn-bg;
      transition : border 400ms ease-out;

      .value.disabled {
        color: lighten($body-color, 20);
      }
      .value {
        overflow: visible;
      }
    }
  }
</style>

